/**
 * 상품 선택 모달(.modal[data-product-picker]) — 매장 케어의 관련상품 고르기.
 *
 * 틀(브랜드 · 검색 · 개수 · 목록)은 PHP 가 그리고, 여기서는 매장 정보 세팅과 같은 검색 프록시
 * (admin-ajax qrshop_setup_products)로 20개씩 받아 목록을 채운다. 모달 body 끝에 닿으면 이어서 불러온다.
 * 여닫기는 initModal.js 가 맡는다(케어 모달 위에 겹쳐 열린다).
 *
 * 어떤 상품이 담겼는지는 부르는 쪽이 안다 — isSelected(id) 로 물어 [Add] 상태를 그리고,
 * 누르면 onToggle(product) 로 알린다. 목록 자체는 모달을 다시 열어도 그대로 둔다(검색을 되풀이하지 않는다).
 */

import { postForm } from './partnerForm';

const PAGE_SIZE = 20;

/**
 * @param {{
 *   dialog: HTMLDialogElement,
 *   root: HTMLElement,
 *   action?: string,
 *   isSelected: (productId: number) => boolean,
 *   onToggle: (product: { product_id: number, name: string, brand: string, thumbnail: string }) => void,
 * }} options root 는 data-ajax-url · data-nonce 를 가진 요소
 * @return {{ sync: () => void }}
 */
export const createProductPicker = ({
  dialog,
  root,
  action = 'qrshop_setup_products',
  isSelected,
  onToggle,
}) => {
  const body = dialog.querySelector('.modal__body');
  const brand = dialog.querySelector('[data-product-brand]');
  const keyword = dialog.querySelector('[data-product-keyword]');
  const clear = dialog.querySelector('[data-search-clear]');
  const list = dialog.querySelector('[data-product-list]');
  const total = dialog.querySelector('[data-product-total]');
  const selected = dialog.querySelector('[data-product-selected]');

  let page = 0;
  let hasMore = false;
  let loading = false;
  let brandsFilled = false;
  let started = false;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);

    if (className) {
      node.className = className;
    }

    if (text !== undefined) {
      node.textContent = text;
    }

    return node;
  };

  /** 담긴 상품의 [Add] 버튼을 켜고(파랗게) 개수를 맞춘다 — 세팅 화면 상품 검색과 같은 모양 */
  const sync = () => {
    let count = 0;

    list.querySelectorAll('[data-product-add]').forEach((button) => {
      const on = isSelected(Number(button.dataset.productAdd));
      button.classList.toggle('qrshop-product-add--on', on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.textContent = 'Add';
    });

    // 목록에 없는(다른 검색에서 담은) 상품도 세야 하므로 개수는 부르는 쪽 기준으로 다시 센다
    count = Number(selected?.dataset.count || 0);

    if (selected) {
      selected.textContent = String(count);
    }
  };

  const render = (items) => {
    items.forEach((product) => {
      const item = el('div', 'qrshop-product-item');
      const thumb = el('div', 'qrshop-product-item__thumb');

      if (product.thumbnail) {
        const image = el('img');
        image.src = product.thumbnail;
        image.alt = product.name || '';
        image.loading = 'lazy';
        thumb.append(image);
      }

      const info = el('div', 'min-w-0 flex-1');

      if (product.brand) {
        info.append(el('span', 'qrshop-product-item__brand', product.brand));
      }

      info.append(el('p', 'qrshop-product-item__name', product.name || ''));

      const add = el('button', 'qrshop-product-add', 'Add');
      add.type = 'button';
      add.dataset.productAdd = String(product.product_id);
      add.addEventListener('click', () => {
        onToggle({
          product_id: Number(product.product_id),
          name: product.name || '',
          brand: product.brand || '',
          thumbnail: product.thumbnail || '',
        });
        sync();
      });

      item.append(thumb, info, add);
      list.append(item);
    });

    sync();
  };

  const search = async ({ reset = true } = {}) => {
    if (loading) {
      return;
    }

    if (reset) {
      page = 0;
      hasMore = false;
      list.textContent = '';
    }

    loading = true;
    const notice = el(
      'p',
      'product-picker__notice',
      reset ? '상품을 불러오는 중입니다…' : '더 불러오는 중…'
    );
    list.append(notice);

    try {
      const result = await postForm(root, action, {
        brand: brand?.value || '',
        q: (keyword?.value || '').trim(),
        page: String(page + 1),
        per_page: String(PAGE_SIZE),
      });

      notice.remove();

      // 브랜드 목록은 첫 응답으로 한 번만 채운다
      if (!brandsFilled && brand) {
        (result.brands || []).forEach(({ slug, name }) => {
          const option = el('option', '', name);
          option.value = slug;
          brand.append(option);
        });
        brandsFilled = true;
      }

      page = Number(result.page) || page + 1;
      hasMore = Boolean(result.has_more);

      if (total) {
        total.textContent = String(result.total ?? 0);
      }

      render(result.items || []);

      if (!list.querySelector('[data-product-add]')) {
        list.append(el('p', 'product-picker__notice', '검색 결과가 없습니다.'));
      }
    } catch (error) {
      notice.remove();
      list.append(el('p', 'product-picker__notice product-picker__notice--error', error.message));
    } finally {
      loading = false;
    }
  };

  brand?.addEventListener('change', () => search());
  clear?.addEventListener('click', () => search());
  keyword?.addEventListener('keydown', (event) => {
    if ('Enter' === event.key) {
      event.preventDefault();
      search();
    }
  });

  // 모달 body 끝에 가까워지면 다음 20개를 이어서 불러온다
  body?.addEventListener('scroll', () => {
    if (!hasMore || loading) {
      return;
    }

    if (body.scrollTop + body.clientHeight >= body.scrollHeight - 120) {
      search({ reset: false });
    }
  });

  // 처음 열릴 때 한 번 검색한다. <dialog> 는 열림을 알리는 이벤트가 없어 open 속성 변화를 본다
  new MutationObserver(() => {
    if (!dialog.open) {
      return;
    }

    sync();

    if (!started) {
      started = true;
      search();
    }

    keyword?.focus();
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

  return { sync };
};
